import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    BRANCH: z.string().optional(),

    DISABLE_EMAIL_ONLY_DURING_WORK_HOURS: z.string().optional(),

    ENV_NAME: z.enum(['local', 'dev', 'staging', 'prod', 'docker']).default('local'),
    SERVICE_NAME: z.string().default('local'),

    PORT: z.string().optional().default('3000'),

    NODE_ENV: z.enum(['development', 'test', 'production']),
    // GOOGLE_REDIRECT_URI: z.string().min(1),

    WORKER_HOST: z.string().optional(),
    CLAUDE_AI_KEY: z.string().optional(),
    REDIS_HOST: z.string().optional(),
    REDIS_PASSWORD: z.string().optional(),

    SUPERADMIN_EMAILS: z
      .string()
      .optional()
      .transform((emails) => {
        if (emails == null) return new Set();

        const split = emails.split('--');
        return new Set(split);
      }),

    FAKE_AUTHED_USER: z.string().optional(),

    // for uploading source maps during build
    SENTRY_AUTH_TOKEN_VAL: z.string().optional(),

    STORAGE_BUCKET: z.string().optional(),

    DB_HOST: z.string().optional(),
    DB_PORT: z.coerce.number().optional(),
    DB_USER: z.string().optional(),
    DB_PASS: z.string().optional(),
    DB_NAME: z.string().optional(),
    DB_SSL_NO: z.coerce.boolean().optional(),

    // OAuth Client ID (Required)
    HUBSPOT_CLIENT_ID: z.string().min(1),
    SLACK_CLIENT_ID: z.string().min(1),
    GMAIL_CLIENT_ID: z.string().min(1),

    DOC_SHARE_URL: z.string().min(1),

    SLACK_ADMIN_CHANNEL: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  shared: {
    // Required
    NEXT_PUBLIC_BACKEND_URL: z.string().min(1),
    // Google and firebase (Required)
    NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT: z.string().min(1),
    // Optional

    // sentry client (Optional)
    NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: z
      .string()
      .optional()
      .transform((s) => (s?.startsWith('BAKED_') ? undefined : s)),

    NEXT_PUBLIC_DEV_WS_PORT: z.coerce.number().optional().default(3001),

    NEXT_PUBLIC_APP_VERSION: z.string().default('main'),
  },

  experimental__runtimeEnv: {
    ...process.env,
    NEXT_PUBLIC_BACKEND_URL: process.env.NEXT_PUBLIC_BACKEND_URL,
    NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT: process.env.NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
    NEXT_PUBLIC_DEV_WS_PORT: process.env.NEXT_PUBLIC_DEV_WS_PORT,
    NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   *
   * because NEXT_PUBLIC vars are inlined and we need them to be injectable
   * doing some hackery with string replacing anything that starts with a key
   * on start, see: replace-variables.sh
   *
   * https://phase.dev/blog/nextjs-public-runtime-variables/
   */
  // runtimeEnv: {
  //   // Server (Required and Optional)
  //   SERVICE_NAME: process.env.SERVICE_NAME,
  //   ENV_NAME: process.env.ENV_NAME,
  //   PORT: process.env.PORT,
  //   NODE_ENV: process.env.NODE_ENV,
  //   WORKER_HOST: process.env.WORKER_HOST,
  //   SUPERADMIN_EMAILS: process.env.SUPERADMIN_EMAILS,
  //   // GOOGLE_REDIRECT_URI: process.env.GOOGLE_REDIRECT_URI,
  //   FAKE_AUTHED_USER: process.env.FAKE_AUTHED_USER,
  //   SENTRY_AUTH_TOKEN_VAL: process.env.SENTRY_AUTH_TOKEN_VAL,
  //   REDIS_HOST: process.env.REDIS_HOST,
  //   REDIS_PASSWORD: process.env.REDIS_PASSWORD,

  //   CLAUDE_AI_KEY: process.env.CLAUDE_AI_KEY,

  //   STORAGE_BUCKET: process.env.STORAGE_BUCKET,

  //   DB_HOST: process.env.DB_HOST,
  //   DB_PORT: process.env.DB_PORT,
  //   DB_USER: process.env.DB_USER,
  //   DB_PASS: process.env.DB_PASS,
  //   DB_NAME: process.env.DB_NAME,
  //   DB_SSL_NO: process.env.DB_SSL_NO,

  //   DISABLE_EMAIL_ONLY_DURING_WORK_HOURS: process.env.DISABLE_EMAIL_ONLY_DURING_WORK_HOURS,

  //   // OAuth Client ID (Required)
  //   HUBSPOT_CLIENT_ID: process.env.HUBSPOT_CLIENT_ID,
  //   SLACK_CLIENT_ID: process.env.SLACK_CLIENT_ID,
  //   GMAIL_CLIENT_ID: process.env.GMAIL_CLIENT_ID,

  //   DOC_SHARE_URL: process.env.DOC_SHARE_URL,

  //   // need to do hackery for production env
  //   // due to next js embedding actual string values
  //   ...(process.env.NODE_ENV == 'production'
  //     ? {
  //         // Required NEXT_PUBLIC
  //         NEXT_PUBLIC_BACKEND_URL: 'BAKED_NEXT_PUBLIC_BACKEND_URL',
  //         // Google and firebase (Required)
  //         NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT: 'BAKED_NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT',
  //         // Optional

  //         // will be injected replacer script
  //         NEXT_PUBLIC_SENTRY_ENVIRONMENT: 'BAKED_NEXT_PUBLIC_SENTRY_ENVIRONMENT',
  //       }
  //     : {
  //         // Required NEXT_PUBLIC
  //         NEXT_PUBLIC_BACKEND_URL: process.env.NEXT_PUBLIC_BACKEND_URL,
  //         // Google and firebase (Required)
  //         NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT: process.env.NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT,

  //         // Optional

  //         // will be injected replacer script
  //         NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
  //       }),
  // },

  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation.
   * This is especially useful for Docker builds.
   */
  // skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined.
   * `SOME_VAR: z.string()` and `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
